import React, { FC, ReactNode, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@cokitchen/cokitchen-components';

type Props = {
  pages: { name: string; url: string; canSkip?: boolean }[];
  children: ReactNode;
  onClick?: (val: number) => Promise<boolean | void> | boolean | void;
  onConfirm: () => void;
  isDisabled?: boolean;
  loading?: boolean;
  hide?: boolean;
  customComponent?: ReactNode;
  disableSideNav?: boolean;
};
const Stepper: FC<Props> = ({
  pages,
  children,
  onClick,
  onConfirm,
  isDisabled,
  loading,
  hide,
  customComponent,
  disableSideNav = false
}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const currPage = pages.findIndex((page) => {
    return pathname.includes(page.url);
  });

  const [activePage, setActivePage] = useState(currPage || 0);

  const onPreviousClick = () => {
    navigate(pages[activePage - 1].url);
    setActivePage(activePage - 1);
  };

  const onSkipClick = () => {
    if (isLastPage()) {
      onConfirm();
    } else {
      navigate(pages[activePage + 1].url, { replace: true });
      setActivePage(activePage + 1);
    }
  };

  useEffect(() => {
    isLastPage();
  }, [pathname]);

  const navigateToUrl = (url: string, index: number) => {
    navigate(url, { replace: true });
    setActivePage(index);
  };
  const style = {
    active: 'text-[#E60A2B]',
    inactive: 'text-[#2E3031]'
  };
  const isLastPage = () => {
    const lastPageUrl = pages[pages?.length - 1]?.url;
    return pathname.includes(lastPageUrl);
  };

  const isFirstPage = () => {
    const firstPageUrl = pages[0]?.url;
    return pathname === firstPageUrl;
  };

  const handleConfirm = async () => {
    if (isLastPage()) {
      onConfirm();
    } else {
      const res = await onClick?.(activePage);
      if (res === false) return;
      navigate(pages[activePage + 1].url, { replace: true });
      setActivePage(activePage + 1);
    }
  };

  return (
    <div className='flex justify-between min-w-full flex-col-reverse md:flex-row'>
      {!hide && <div className='lg:min-w-[300px]'></div>}
      <div className='w-full flex justify-center'>
        <div className='md:max-w-[444px] w-full sm:w-fit'>
          {children}
          <div className='mt-[56px] flex flex-col gap-4 pb-4'>
            <div className='mt-6 w-full'>
              <Button
                className='max-w-[390px] mx-auto'
                color='primary'
                small
                variant='block'
                loading={loading}
                onClick={handleConfirm}
                disabled={isDisabled}>
                {isLastPage() ? 'Proceed' : 'Continue'}
              </Button>
            </div>
            {pages[activePage].canSkip && (
              <div className='w-full'>
                <Button
                  variant='outline'
                  color='gray'
                  small
                  onClick={onSkipClick}
                  style={{
                    color: '#2E3031'
                  }}
                  className='font-medium text-center text-[14px] max-w-[390px] mx-auto hover:!bg-gray-11'>
                  Skip
                </Button>
              </div>
            )}
            {!isFirstPage() && (
              <div className='w-full'>
                <Button
                  color='gray'
                  variant='outline'
                  small
                  onClick={onPreviousClick}
                  style={{
                    color: '#2E3031'
                  }}
                  className='font-medium text-center text-[14px] max-w-[390px] mx-auto hover:!bg-gray-11'>
                  Previous
                </Button>
              </div>
            )}
            {isFirstPage() && customComponent}
          </div>
        </div>
      </div>

      <div className='w-full basis-1/3 mt-24 flex flex-row md:flex-col justify-center md:justify-start'>
        {pages.map(({ name, url }, index) => (
          <p
            key={index}
            onClick={() => {
              if (activePage >= index || !disableSideNav) navigateToUrl(url, index);
            }}
            className={`${activePage === index ? style.active : style.inactive} ${
              activePage >= index ? 'border-[#E60A2B]' : 'border-[#EFF0F0]'
            } cursor-pointer ml-0 mb-6 md:mb-0 md:ml-4 text-[14px] border-l-0 border-b-2 md:border-l-2 md:border-b-0 pl-[14px] py-[10px] `}>
            {name}
          </p>
        ))}
      </div>
    </div>
  );
};

export default Stepper;
